import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { csvCell, defaultTickSize, INSTRUMENT_CSV_COLUMNS, instrumentImportTemplate, MIN_IMPORT_COLUMNS, parseInstrumentImport, priceAge, priceProxyNote, type MarketInstrumentDefaults } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import type { Instrument } from '../lib/types';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Select, Textarea } from '../components/ui';
import { PriceLabel } from '../components/PriceMark';

/** INT-ACC-01 / INT-ACC-02: markets (exchange clock, sessions, deadline, holidays) and the symbol list per market. */

type Deadline = { mode: 'BEFORE_OPEN'; minutes: number } | { mode: 'FIXED_TIME'; time: string; tz: string };
interface Session {
  weekday: number;
  opensAt: string;
  closesAt: string;
  label?: string;
}
interface Holiday {
  id: string;
  date: string;
  name: string;
  earlyCloseAt: string | null;
}
interface MarketAdmin {
  id: string;
  code: string;
  name: string;
  timezone: string;
  currency: string;
  is24h: boolean;
  active: boolean;
  analysisDeadline: Deadline;
  sessions: Session[];
  holidays: Holiday[];
  instrumentDefaults: MarketInstrumentDefaults;
  instruments: number;
  accounts: number;
}
type InstrumentRow = Instrument & { active: boolean; price?: { price: string | null; source: string; asOf: string | null; fetchedAt: string | null; lastError: string | null; lastErrorAt: string | null } | null };

/** GET /prices/status — the interim delayed price source (INT-SYN-04, D23). */
interface PriceStatus {
  source: 'yahoo' | 'manual';
  provider: { name: string; short: string; note: string };
  pollSeconds: number;
  staleMinutes: number;
  lastRunAt: string | null;
  lastOkAt: string | null;
  failures: number;
  backoffUntil: string | null;
  lastError: string | null;
  polling: string[];
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ZONES = ['Asia/Karachi', 'America/New_York', 'America/Chicago', 'Europe/London', 'Europe/Frankfurt', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo', 'Australia/Sydney', 'UTC'];
const UNITS = ['SHARES', 'LOTS', 'CONTRACTS', 'UNITS'] as const;
const NEW = '__new__';

function Ok({ text }: { text: string | null }) {
  return text ? <p className="m-0 text-sm text-up">{text}</p> : null;
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  URL.revokeObjectURL(url);
}

export function Markets() {
  const me = useMe();
  const isAdmin = hasRole(me.data?.roles, 'ADMIN');
  const markets = useQuery({ queryKey: ['admin-markets'], queryFn: () => api<MarketAdmin[]>('/admin/markets') });
  const [selected, setSelected] = useState<string | null>(null);
  useEffect(() => {
    if (!selected && markets.data?.length) setSelected(markets.data[0]!.id);
  }, [markets.data, selected]);
  const market = markets.data?.find((m) => m.id === selected) ?? null;

  return (
    <>
      <PageHeader
        title="Markets & symbols"
        sub="Each market sets the clock, trading hours, holidays and analysis deadline for its symbols"
        right={isAdmin && <Button variant="primary" onClick={() => setSelected(NEW)}>+ New market</Button>}
      />
      <ErrorText error={markets.error ? errorText(markets.error) : null} />
      <div role="tablist" aria-label="Markets" className="flex flex-wrap gap-1.5">
        {markets.data?.map((m) => (
          <button
            key={m.id}
            role="tab"
            aria-selected={m.id === selected}
            onClick={() => setSelected(m.id)}
            className={cx('rounded-lg border px-3.5 py-2 text-sm', m.id === selected ? 'border-accent bg-accent-bg font-semibold text-ink' : 'border-line2 bg-panel text-sub', !m.active && 'opacity-60')}
          >
            {m.code} <span className="font-normal text-muted">· {m.instruments}</span>
            {!m.active && <span className="ml-1 text-xs text-muted">(off)</span>}
          </button>
        ))}
        {selected === NEW && <span className="rounded-lg border border-accent bg-accent-bg px-3.5 py-2 text-sm font-semibold">New market</span>}
      </div>

      <PricesPanel />
      {selected === NEW && <MarketForm key="new" market={null} canEdit={isAdmin} onSaved={(id) => setSelected(id)} />}
      {market && (
        <>
          <div className="grid items-start gap-3.5 xl:grid-cols-[3fr_2fr]">
            <MarketForm key={market.id} market={market} canEdit={isAdmin} onSaved={() => undefined} />
            <Holidays market={market} canEdit={isAdmin} />
          </div>
          <Symbols key={market.id} market={market} canEdit={isAdmin} />
        </>
      )}
    </>
  );
}

/** Delayed prices: which source, when it last worked, what it polls now (symbols with open trades or active setups, in session). */
function PricesPanel() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const q = useQuery({ queryKey: ['prices-status'], queryFn: () => api<PriceStatus>('/prices/status'), refetchInterval: 60_000 });
  const d = q.data;
  if (!d) return <ErrorText error={q.error ? errorText(q.error) : null} />;
  const failing = d.failures > 0;
  return (
    <Card tone={failing ? 'warn' : undefined} className="flex flex-col gap-1.5 px-4 py-3 text-sm">
      <span className="flex flex-wrap items-center gap-2">
        <strong>Delayed prices</strong>
        <Pill tone={d.source === 'manual' ? 'neutral' : failing ? 'warn' : 'up'}>{d.source === 'manual' ? 'Manual only' : `${d.provider.name} · every ${d.pollSeconds} s`}</Pill>
        <span className="text-xs text-muted">
          last good fetch {d.lastOkAt ? fmtTime(d.lastOkAt, tz, true) : 'never'} · stale after {d.staleMinutes} min · <Link to="/admin/settings">change in the settings console</Link>
        </span>
      </span>
      <span className="text-xs text-sub">
        {d.source === 'manual'
          ? 'Fetching is off: traders type prices in their reports.'
          : d.polling.length
            ? `Polling now: ${d.polling.join(', ')}.`
            : 'Nothing to poll right now — only symbols with a price symbol and an open trade or active setup, while their market is open.'}{' '}
        {d.provider.note} PSX and symbols without a price symbol stay manual.
      </span>
      {failing && (
        <span className="text-xs text-warn">
          {d.failures} failed round{d.failures === 1 ? '' : 's'} in a row: {d.lastError}
          {d.backoffUntil && ` · next try after ${fmtTime(d.backoffUntil, tz)}`}
        </span>
      )}
    </Card>
  );
}

function MarketForm({ market, canEdit, onSaved }: { market: MarketAdmin | null; canEdit: boolean; onSaved: (id: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState(() => ({
    code: market?.code ?? '',
    name: market?.name ?? '',
    timezone: market?.timezone ?? 'Asia/Karachi',
    currency: market?.currency ?? '',
    is24h: market?.is24h ?? false,
    active: market?.active ?? true,
    deadline: market?.analysisDeadline ?? ({ mode: 'BEFORE_OPEN', minutes: 30 } as Deadline),
    sessions: (market?.sessions ?? [1, 2, 3, 4, 5].map((weekday) => ({ weekday, opensAt: '09:30', closesAt: '16:00' }))) as Session[],
    defaults: {
      tickSize: market?.instrumentDefaults.tickSize ?? '',
      pointValue: market?.instrumentDefaults.pointValue ?? '',
      sizeUnit: market?.instrumentDefaults.sizeUnit ?? '',
      minSize: market?.instrumentDefaults.minSize ?? '',
      sizeStep: market?.instrumentDefaults.sizeStep ?? '',
    },
  }));
  const setDefault = (k: keyof typeof f.defaults, v: string) => setF({ ...f, defaults: { ...f.defaults, [k]: v } });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, timezone: f.timezone, currency: f.currency, is24h: f.is24h, analysisDeadline: f.deadline, sessions: f.sessions.map((s) => ({ ...s, label: s.label || undefined })), instrumentDefaults: f.defaults };
      return market ? api<MarketAdmin>(`/markets/${market.id}`, { method: 'PATCH', body: { ...body, active: f.active } }) : api<MarketAdmin>('/markets', { body: { ...body, code: f.code } });
    },
    onSuccess: (m) => {
      void qc.invalidateQueries({ queryKey: ['admin-markets'] });
      void qc.invalidateQueries({ queryKey: ['markets'] });
      onSaved(m.id);
    },
  });
  const setSession = (i: number, patch: Partial<Session>) => setF({ ...f, sessions: f.sessions.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const monday = f.sessions.filter((s) => s.weekday === 1);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="m-0 text-[15px] font-semibold">{market ? `${market.code} · ${market.name}` : 'New market'}</h2>
      {market && <p className="m-0 text-xs text-muted">{market.instruments} symbols · {market.accounts} accounts</p>}
      <fieldset disabled={!canEdit} className="m-0 flex flex-col gap-3 border-0 p-0">
        <div className="grid gap-3 sm:grid-cols-2">
          {!market && (
            <Field label="Code" hint="Short and unique, e.g. ASX or CRYPTO">
              <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
            </Field>
          )}
          <Field label="Name" className={market ? 'sm:col-span-2' : undefined}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="ASX Equities" /></Field>
          <Field label="Exchange timezone" hint="IANA name; trading hours below are in this zone">
            <Input list="vtf-zones" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} />
          </Field>
          <Field label="Base currency" hint="Default for new accounts and the quote currency of imported symbols">
            <Input value={f.currency} maxLength={3} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} placeholder="AUD" />
          </Field>
        </div>
        <datalist id="vtf-zones">{ZONES.map((z) => <option key={z} value={z} />)}</datalist>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Daily analysis deadline">
            <Select
              value={f.deadline.mode}
              onChange={(e) => setF({ ...f, deadline: e.target.value === 'FIXED_TIME' ? { mode: 'FIXED_TIME', time: '07:00', tz: f.timezone } : { mode: 'BEFORE_OPEN', minutes: 30 } })}
            >
              <option value="BEFORE_OPEN">Minutes before the session opens</option>
              <option value="FIXED_TIME">A fixed time of day</option>
            </Select>
          </Field>
          {f.deadline.mode === 'BEFORE_OPEN' ? (
            <Field label="Minutes before open">
              <Input mono inputMode="numeric" value={String(f.deadline.minutes)} onChange={(e) => setF({ ...f, deadline: { mode: 'BEFORE_OPEN', minutes: Number(e.target.value.replace(/\D/g, '') || 0) } })} />
            </Field>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Time"><Input mono value={f.deadline.time} onChange={(e) => setF({ ...f, deadline: { ...(f.deadline as Extract<Deadline, { mode: 'FIXED_TIME' }>), time: e.target.value } })} placeholder="07:00" /></Field>
              <Field label="In timezone"><Input list="vtf-zones" value={f.deadline.tz} onChange={(e) => setF({ ...f, deadline: { ...(f.deadline as Extract<Deadline, { mode: 'FIXED_TIME' }>), tz: e.target.value } })} /></Field>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.is24h} onChange={(e) => setF({ ...f, is24h: e.target.checked })} /> Trades around the clock (Forex-style)</label>
          {market && <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>}
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="m-0 text-sm font-semibold">Symbol defaults</h3>
          <p className="m-0 text-xs text-muted">
            A symbol import fills these in for new symbols when the file leaves them out (the quote currency is the base currency above). Leave one blank and imports must give it.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Field label="Default tick size"><Input mono value={f.defaults.tickSize} onChange={(e) => setDefault('tickSize', e.target.value)} placeholder="0.01" /></Field>
            <Field label="Default point value"><Input mono value={f.defaults.pointValue} onChange={(e) => setDefault('pointValue', e.target.value)} placeholder="1" /></Field>
            <Field label="Default size unit">
              <Select value={f.defaults.sizeUnit} onChange={(e) => setDefault('sizeUnit', e.target.value)}>
                <option value="">none</option>
                {UNITS.map((u) => <option key={u} value={u}>{u.toLowerCase()}</option>)}
              </Select>
            </Field>
            <Field label="Default min size"><Input mono value={f.defaults.minSize} onChange={(e) => setDefault('minSize', e.target.value)} placeholder="1" /></Field>
            <Field label="Default size step"><Input mono value={f.defaults.sizeStep} onChange={(e) => setDefault('sizeStep', e.target.value)} placeholder="1" /></Field>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="m-0 text-sm font-semibold">Weekly trading sessions</h3>
            <div className="flex gap-2">
              {monday.length > 0 && (
                <Button
                  onClick={() => setF({ ...f, sessions: [...f.sessions.filter((s) => s.weekday === 1 || s.weekday > 5), ...[2, 3, 4, 5].flatMap((weekday) => monday.map((s) => ({ ...s, weekday })))].sort((a, b) => a.weekday - b.weekday || a.opensAt.localeCompare(b.opensAt)) })}
                >
                  Copy Monday to Tue–Fri
                </Button>
              )}
              <Button onClick={() => setF({ ...f, sessions: [...f.sessions, { weekday: 1, opensAt: '09:30', closesAt: '16:00' }] })}>+ Session</Button>
            </div>
          </div>
          <p className="m-0 text-xs text-muted">Times are in the exchange timezone. For an overnight session, end the day at 24:00 and start the next at 00:00. Add a second row for a day with a lunch break.</p>
          {f.sessions.length === 0 && <Empty>No sessions — nothing will be due for this market.</Empty>}
          {f.sessions.map((s, i) => (
            <div key={i} className="grid grid-cols-[90px_1fr_1fr_1.4fr_auto] items-center gap-2">
              <Select aria-label="Day" value={s.weekday} onChange={(e) => setSession(i, { weekday: Number(e.target.value) })}>
                {DAYS.map((d, j) => <option key={d} value={j + 1}>{d}</option>)}
              </Select>
              <Input mono aria-label="Opens" value={s.opensAt} onChange={(e) => setSession(i, { opensAt: e.target.value })} />
              <Input mono aria-label="Closes" value={s.closesAt} onChange={(e) => setSession(i, { closesAt: e.target.value })} />
              <Input aria-label="Label" value={s.label ?? ''} placeholder="label (optional)" onChange={(e) => setSession(i, { label: e.target.value })} />
              <Button aria-label={`Remove ${DAYS[s.weekday - 1]} session`} onClick={() => setF({ ...f, sessions: f.sessions.filter((_, j) => j !== i) })}>✕</Button>
            </div>
          ))}
        </div>
      </fieldset>
      <ErrorText error={save.error ? errorText(save.error) : null} />
      <Ok text={save.isSuccess ? 'Saved. Deadlines and reports follow the new hours from the next check (within a minute).' : null} />
      {canEdit && (
        <Button variant="primary" className="self-start" onClick={() => save.mutate()} disabled={save.isPending}>
          {market ? 'Save market' : 'Create market'}
        </Button>
      )}
    </Card>
  );
}

function Holidays({ market, canEdit }: { market: MarketAdmin; canEdit: boolean }) {
  const qc = useQueryClient();
  const [one, setOne] = useState({ date: '', name: '', earlyCloseAt: '' });
  const [paste, setPaste] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const done = () => void qc.invalidateQueries({ queryKey: ['admin-markets'] });
  const add = useMutation({ mutationFn: (holidays: { date: string; name: string; earlyCloseAt?: string | null }[]) => api(`/markets/${market.id}/holidays`, { body: { holidays } }), onSuccess: done });
  const remove = useMutation({ mutationFn: (id: string) => api(`/markets/${market.id}/holidays/${id}`, { method: 'DELETE' }), onSuccess: done });
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = market.holidays.filter((h) => h.date >= today);
  const past = market.holidays.filter((h) => h.date < today).slice(-3);

  const addPasted = () => {
    const rows = paste.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const out: { date: string; name: string; earlyCloseAt: string | null }[] = [];
    for (const [i, l] of rows.entries()) {
      const m = l.match(/^(\d{4}-\d{2}-\d{2})\s*[,;\t]\s*(.+?)(?:\s*[,;\t]\s*(\d{2}:\d{2}))?$/);
      if (!m) return setParseError(`Line ${i + 1}: use "YYYY-MM-DD, Name" or "YYYY-MM-DD, Name, HH:MM" for a half day`);
      out.push({ date: m[1]!, name: m[2]!, earlyCloseAt: m[3] ?? null });
    }
    setParseError(null);
    if (out.length) add.mutate(out, { onSuccess: () => setPaste('') });
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="m-0 text-[15px] font-semibold">Holidays · {market.code}</h2>
      <p className="m-0 text-xs text-muted">No analysis or session reports are due on a holiday. A half day keeps the morning session and closes early.</p>
      <div className="flex flex-col">
        {upcoming.length === 0 && <span className="text-sm text-sub">No upcoming holidays entered.</span>}
        {[...past, ...upcoming].map((h) => (
          <div key={h.id} className={cx('flex items-center justify-between gap-2 border-t border-[#1a1f27] py-2 first:border-t-0', h.date < today && 'opacity-50')}>
            <div className="flex flex-col">
              <span className="font-mono text-sm">{h.date}</span>
              <span className="text-xs text-sub">{h.name}</span>
            </div>
            <div className="flex items-center gap-2">
              {h.earlyCloseAt ? <Pill tone="warn">closes {h.earlyCloseAt}</Pill> : <Pill>closed</Pill>}
              {canEdit && <Button aria-label={`Remove ${h.name}`} onClick={() => remove.mutate(h.id)} disabled={remove.isPending}>✕</Button>}
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <>
          <div className="grid grid-cols-[1fr_1.4fr] gap-2">
            <Field label="Date"><Input type="date" value={one.date} onChange={(e) => setOne({ ...one, date: e.target.value })} /></Field>
            <Field label="Name"><Input value={one.name} onChange={(e) => setOne({ ...one, name: e.target.value })} placeholder="Eid ul-Fitr" /></Field>
            <Field label="Half day: closes at (optional)"><Input mono value={one.earlyCloseAt} onChange={(e) => setOne({ ...one, earlyCloseAt: e.target.value })} placeholder="13:00" /></Field>
            <div className="flex items-end">
              <Button onClick={() => add.mutate([{ date: one.date, name: one.name, earlyCloseAt: one.earlyCloseAt || null }], { onSuccess: () => setOne({ date: '', name: '', earlyCloseAt: '' }) })} disabled={!one.date || !one.name || add.isPending}>
                Add holiday
              </Button>
            </div>
          </div>
          <Field label="Or paste a list" hint={'One per line: 2026-12-25, Christmas Day — add ", 13:00" for a half day'}>
            <Textarea rows={3} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'2027-02-05, Kashmir Day\n2027-03-23, Pakistan Day'} />
          </Field>
          <Button className="self-start" onClick={addPasted} disabled={!paste.trim() || add.isPending}>Add pasted holidays</Button>
          <ErrorText error={parseError ?? (add.error ? errorText(add.error) : remove.error ? errorText(remove.error) : null)} />
        </>
      )}
    </Card>
  );
}

const blank = { symbol: '', displayName: '', quoteCurrency: '', tickSize: '', pointValue: '', sizeUnit: 'SHARES', minSize: '1', sizeStep: '1', tvTicker: '', priceSymbol: '' };

/** The latest delayed price for a symbol, labelled with source and time; or the last error. */
function PriceCell({ p, staleMinutes, tz }: { p: InstrumentRow['price']; staleMinutes: number; tz: string }) {
  if (!p) return <span className="text-xs text-muted">—</span>;
  return (
    <span className="flex flex-col">
      {p.price && p.asOf && (
        <>
          <span className="font-mono">{p.price}</span>
          <PriceLabel mark={{ source: p.source, sourceName: p.source, asOf: p.asOf, stale: priceAge(p.asOf, Date.now(), staleMinutes).stale }} tz={tz} />
        </>
      )}
      {p.lastError && (
        <span className="max-w-[240px] text-xs text-warn" title={p.lastError}>
          {p.lastError.length > 70 ? `${p.lastError.slice(0, 67)}…` : p.lastError}
        </span>
      )}
    </span>
  );
}

function Symbols({ market, canEdit }: { market: MarketAdmin; canEdit: boolean }) {
  const qc = useQueryClient();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const status = useQuery({ queryKey: ['prices-status'], queryFn: () => api<PriceStatus>('/prices/status'), refetchInterval: 60_000 });
  const staleMinutes = status.data?.staleMinutes ?? 20;
  const [q, setQ] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // INT-ACC-02: blank trading fields are filled from the market's symbol defaults (shown as placeholders)
  const md = market.instrumentDefaults;
  const fresh = () => ({ ...blank, quoteCurrency: market.currency, sizeUnit: (md.sizeUnit ?? 'SHARES') as string, minSize: '', sizeStep: '' });
  const [draft, setDraft] = useState(fresh);
  const [csv, setCsv] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const list = useQuery({ queryKey: ['admin-instruments', market.id], queryFn: () => api<InstrumentRow[]>(`/admin/instruments?marketId=${market.id}&includeRetired=1`) });
  const refresh = () => ['admin-instruments', 'admin-markets', 'instruments'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
  const create = useMutation({ mutationFn: () => api('/instruments', { body: { ...draft, marketId: market.id } }), onSuccess: () => { refresh(); setDraft(fresh()); } });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: object }) => api(`/instruments/${id}`, { method: 'PATCH', body }), onSuccess: () => { refresh(); setEditing(null); } });
  const importCsv = useMutation({ mutationFn: () => api<{ format: 'csv' | 'watchlist'; created: number; updated: number; mappingsChanged: number; errors: string[] }>('/instruments/import', { body: { marketId: market.id, csv } }), onSuccess: refresh });

  const rows = useMemo(() => {
    const needle = q.trim().toUpperCase();
    return (list.data ?? []).filter((i) => (showRetired || i.active) && (!needle || i.symbol.includes(needle) || i.displayName.toUpperCase().includes(needle)));
  }, [list.data, q, showRetired]);
  const retired = (list.data ?? []).filter((i) => !i.active).length;
  // same normaliser as the server (INT-ACC-02): a preview of what the import will read; the server's result is authoritative
  const preview = useMemo(() => (csv.trim() ? parseInstrumentImport(csv, { currency: market.currency, defaults: market.instrumentDefaults }) : null), [csv, market.currency, market.instrumentDefaults]);
  const d = market.instrumentDefaults;
  const defaultsText = [
    `currency ${market.currency || '— (file must give it)'}`,
    `tick ${d.tickSize ?? '— (file must give it)'}`,
    `point value ${d.pointValue ?? '— (file must give it)'}`,
    `unit ${d.sizeUnit?.toLowerCase() ?? '— (file must give it)'}`,
    `min ${d.minSize ?? '0'}`,
    `step ${d.sizeStep ?? '1'}`,
  ].join(' · ');

  const toCsv = (items: Pick<InstrumentRow, (typeof INSTRUMENT_CSV_COLUMNS)[number]>[]) =>
    [INSTRUMENT_CSV_COLUMNS.join(','), ...items.map((i) => INSTRUMENT_CSV_COLUMNS.map((c) => csvCell(String(i[c] ?? ''))).join(','))].join('\r\n') + '\r\n';
  const template = () => download(`vtf-symbols-template-${market.code}.csv`, instrumentImportTemplate(market, (list.data ?? []).map((i) => i.tvTicker)));

  return (
    <Card>
      <CardHeader
        title={<>Symbols · {market.code} <span className="font-normal text-muted">· {(list.data ?? []).length - retired} active{retired ? `, ${retired} retired` : ''}</span></>}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-44"><Input aria-label="Search symbols" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="py-1.5" /></div>
            {retired > 0 && <label className="flex items-center gap-1.5 text-xs text-sub"><input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> show retired</label>}
            <Button onClick={() => download(`vtf-symbols-${market.code}.csv`, toCsv(list.data ?? []))} disabled={!list.data?.length}>Export CSV</Button>
          </div>
        }
      />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs text-muted">
              {['Symbol', 'Name', 'Quote', 'Tick size', 'Point value', 'Unit', 'Min', 'Step', 'TradingView', 'Price symbol', 'Delayed price', ''].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((i) => (editing === i.id ? <EditRow key={i.id} item={i} onSave={(body) => update.mutate({ id: i.id, body })} onCancel={() => setEditing(null)} busy={update.isPending} /> : (
              <tr key={i.id} className={cx('border-t border-[#1a1f27]', !i.active && 'opacity-50')}>
                <td className="px-4 py-2.5 font-semibold">{i.symbol}{!i.active && <Pill className="ml-2">retired</Pill>}</td>
                <td className="px-4 py-2.5 text-ink2">{i.displayName}</td>
                <td className="px-4 py-2.5">{i.quoteCurrency}</td>
                <td className="px-4 py-2.5 font-mono">{i.tickSize}</td>
                <td className="px-4 py-2.5 font-mono">{i.pointValue}</td>
                <td className="px-4 py-2.5 text-xs">{i.sizeUnit.toLowerCase()}</td>
                <td className="px-4 py-2.5 font-mono">{i.minSize}</td>
                <td className="px-4 py-2.5 font-mono">{i.sizeStep}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{i.tvTicker ?? <span className="text-muted">plain symbol</span>}</td>
                <td className="px-4 py-2.5 font-mono text-xs">
                  {i.priceSymbol ?? <span className="text-muted">manual</span>}
                  {priceProxyNote(i.priceSymbol) && <span className="block font-sans text-muted">futures proxy</span>}
                </td>
                <td className="px-4 py-2.5"><PriceCell p={i.price} staleMinutes={staleMinutes} tz={tz} /></td>
                <td className="px-4 py-2.5 text-right">
                  {canEdit && (
                    <div className="flex justify-end gap-1.5">
                      <Button onClick={() => setEditing(i.id)}>Edit</Button>
                      <Button onClick={() => update.mutate({ id: i.id, body: { active: !i.active } })} disabled={update.isPending}>{i.active ? 'Retire' : 'Restore'}</Button>
                    </div>
                  )}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
        {list.isSuccess && rows.length === 0 && <Empty>{q ? 'No symbols match.' : 'No symbols yet — add one below or import a CSV.'}</Empty>}
      </div>
      <ErrorText error={update.error ? errorText(update.error) : null} />

      {canEdit && (
        <div className="grid gap-4 border-t border-line p-4 xl:grid-cols-2">
          <div className="flex flex-col gap-3">
            <h3 className="m-0 text-sm font-semibold">Add a symbol</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Field label="Symbol"><Input value={draft.symbol} onChange={(e) => setDraft({ ...draft, symbol: e.target.value.toUpperCase() })} placeholder="ENGRO" /></Field>
              <Field label="Name" className="col-span-2 sm:col-span-3"><Input value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} placeholder="Engro Corporation" /></Field>
              <Field label="Quote currency"><Input value={draft.quoteCurrency} maxLength={3} onChange={(e) => setDraft({ ...draft, quoteCurrency: e.target.value.toUpperCase() })} /></Field>
              <Field label="Tick size" hint="Smallest price step · blank = market default"><Input mono value={draft.tickSize} onChange={(e) => setDraft({ ...draft, tickSize: e.target.value })} placeholder={defaultTickSize(draft.symbol, md) ?? "required"} /></Field>
              <Field label="Point value" hint="1.0 move × 1 unit · blank = market default"><Input mono value={draft.pointValue} onChange={(e) => setDraft({ ...draft, pointValue: e.target.value })} placeholder={md.pointValue ?? "required"} /></Field>
              <Field label="Size unit">
                <Select value={draft.sizeUnit} onChange={(e) => setDraft({ ...draft, sizeUnit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{u.toLowerCase()}</option>)}</Select>
              </Field>
              <Field label="Min size"><Input mono value={draft.minSize} onChange={(e) => setDraft({ ...draft, minSize: e.target.value })} placeholder={md.minSize ?? "1"} /></Field>
              <Field label="Size step"><Input mono value={draft.sizeStep} onChange={(e) => setDraft({ ...draft, sizeStep: e.target.value })} placeholder={md.sizeStep ?? "1"} /></Field>
              <Field label="TradingView ticker" className="col-span-2" hint="For strategy signals, e.g. OANDA:XAUUSD"><Input value={draft.tvTicker} onChange={(e) => setDraft({ ...draft, tvTicker: e.target.value.toUpperCase() })} placeholder="NASDAQ:AAPL" /></Field>
              <Field label="Price symbol" className="col-span-2" hint="Delayed prices, e.g. EURUSD=X or GC=F; empty = manual"><Input value={draft.priceSymbol} onChange={(e) => setDraft({ ...draft, priceSymbol: e.target.value.toUpperCase() })} placeholder="AAPL" /></Field>
            </div>
            <p className="m-0 text-xs text-muted">
              Point value is what a 1.0 price move is worth for one unit of size, in the quote currency: 1 for a share, 100,000 for a standard FX lot, 100 for a 100 oz gold lot,
              50 for an ES futures contract. Position sizing and the risk check use these numbers, so match your broker&apos;s contract specs.
            </p>
            <ErrorText error={create.error ? errorText(create.error) : null} />
            <Ok text={create.isSuccess ? 'Symbol added. Assign it to traders on the Admin page.' : null} />
            <Button variant="primary" className="self-start" onClick={() => create.mutate()} disabled={create.isPending || !draft.symbol}>Add symbol</Button>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="m-0 text-sm font-semibold">Import a CSV into {market.code}</h3>
            <p className="m-0 text-xs text-muted">
              Only a <span className="font-mono">symbol</span> column is needed — add <span className="font-mono">exchange</span> and <span className="font-mono">currency</span>, and <span className="font-mono">name</span> if you like
              (header: <span className="font-mono">{MIN_IMPORT_COLUMNS.join(',')}</span>; <span className="font-mono">PSX:OGDC</span> in the symbol column works too). Or paste a TradingView watchlist export as it is
              (<span className="font-mono">PSX:OGDC,PSX:MARI</span>). VTF sets the TradingView ticker to EXCHANGE:SYMBOL and fills the rest from this market&apos;s defaults.
            </p>
            <p className="m-0 text-xs text-sub" data-testid="import-defaults">New symbols in {market.code} get: {defaultsText}{d.sizeUnit === 'LOTS' ? ' (JPY pairs: tick 0.001)' : ''}.</p>
            <p className="m-0 text-xs text-muted">
              A filled-in column always wins ({INSTRUMENT_CSV_COLUMNS.slice(1).join(', ')}). Existing symbols are updated only with what the file gives (and restored if retired), so their custom values stay. Leave
              tvTicker or priceSymbol out to keep mappings; a blank cell clears one. Bad rows are listed and skipped.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={template}>Download template</Button>
              <Button onClick={() => fileRef.current?.click()}>Choose file…</Button>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) void file.text().then(setCsv); e.target.value = ''; }} />
            </div>
            <Textarea rows={5} aria-label="CSV content" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={`${MIN_IMPORT_COLUMNS.join(',')}\n— or paste PSX:OGDC,PSX:MARI —`} className="font-mono text-xs" />
            {preview && (
              <p className={cx('m-0 text-xs', preview.error ? 'text-warn' : 'text-sub')} data-testid="import-preview">
                {preview.error ??
                  `${preview.format === 'watchlist' ? 'TradingView watchlist' : 'CSV'} · ${preview.rows.length} symbol${preview.rows.length === 1 ? '' : 's'}: ${preview.rows.slice(0, 4).map((r) => r.supplied.tvTicker || r.symbol).join(', ')}${preview.rows.length > 4 ? ', …' : ''}${preview.rows.some((r) => r.errors.length) ? ` · ${preview.rows.filter((r) => r.errors.length).length} with errors` : ''}`}
              </p>
            )}
            <Button variant="primary" className="self-start" onClick={() => importCsv.mutate()} disabled={!csv.trim() || importCsv.isPending}>Import</Button>
            <ErrorText error={importCsv.error ? errorText(importCsv.error) : null} />
            {importCsv.data && (
              <div className="flex flex-col gap-1 rounded-lg bg-raised p-3 text-sm">
                <span className="text-up">{importCsv.data.created} added · {importCsv.data.updated} updated{importCsv.data.mappingsChanged ? ` · ${importCsv.data.mappingsChanged} mapping${importCsv.data.mappingsChanged === 1 ? '' : 's'} changed` : ''}</span>
                {importCsv.data.errors.map((e) => <span key={e} className="text-xs text-warn">{e}</span>)}
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function EditRow({ item, onSave, onCancel, busy }: { item: InstrumentRow; onSave: (body: object) => void; onCancel: () => void; busy: boolean }) {
  const [d, setD] = useState({ displayName: item.displayName, quoteCurrency: item.quoteCurrency, tickSize: item.tickSize, pointValue: item.pointValue, sizeUnit: item.sizeUnit, minSize: item.minSize, sizeStep: item.sizeStep, tvTicker: item.tvTicker ?? '', priceSymbol: item.priceSymbol ?? '' });
  const cell = 'px-2 py-2';
  return (
    <tr className="border-t border-[#1a1f27] bg-raised">
      <td className="px-4 py-2 font-semibold">{item.symbol}</td>
      <td className={cell}><Input aria-label="Name" value={d.displayName} onChange={(e) => setD({ ...d, displayName: e.target.value })} className="py-1.5" /></td>
      <td className={cell}><Input aria-label="Quote currency" value={d.quoteCurrency} maxLength={3} onChange={(e) => setD({ ...d, quoteCurrency: e.target.value.toUpperCase() })} className="w-16 py-1.5" /></td>
      <td className={cell}><Input mono aria-label="Tick size" value={d.tickSize} onChange={(e) => setD({ ...d, tickSize: e.target.value })} className="w-24 py-1.5" /></td>
      <td className={cell}><Input mono aria-label="Point value" value={d.pointValue} onChange={(e) => setD({ ...d, pointValue: e.target.value })} className="w-24 py-1.5" /></td>
      <td className={cell}>
        <Select aria-label="Size unit" value={d.sizeUnit} onChange={(e) => setD({ ...d, sizeUnit: e.target.value })} className="py-1.5">{UNITS.map((u) => <option key={u} value={u}>{u.toLowerCase()}</option>)}</Select>
      </td>
      <td className={cell}><Input mono aria-label="Min size" value={d.minSize} onChange={(e) => setD({ ...d, minSize: e.target.value })} className="w-16 py-1.5" /></td>
      <td className={cell}><Input mono aria-label="Size step" value={d.sizeStep} onChange={(e) => setD({ ...d, sizeStep: e.target.value })} className="w-16 py-1.5" /></td>
      <td className={cell}><Input aria-label="TradingView ticker" value={d.tvTicker} placeholder="OANDA:EURUSD" onChange={(e) => setD({ ...d, tvTicker: e.target.value.toUpperCase() })} className="w-36 py-1.5 font-mono text-xs" /></td>
      <td className={cell}><Input aria-label="Price symbol" value={d.priceSymbol} placeholder="EURUSD=X" onChange={(e) => setD({ ...d, priceSymbol: e.target.value.toUpperCase() })} className="w-28 py-1.5 font-mono text-xs" /></td>
      <td className={cell} />
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1.5">
          <Button variant="primary" onClick={() => onSave(d)} disabled={busy}>Save</Button>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </td>
    </tr>
  );
}
